import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Clock, Play, Pause, Bell, BellOff, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import NeonButton from "@/components/NeonButton";

const presetDurations = [
  { value: 5, label: "5 min" },
  { value: 10, label: "10 min" },
  { value: 15, label: "15 min" },
  { value: 20, label: "20 min" },
  { value: 30, label: "30 min" },
  { value: 45, label: "45 min" },
  { value: 60, label: "1 hour" },
  { value: 90, label: "1.5 hours" },
  { value: 120, label: "2 hours" },
];

const SessionTimer = ({ 
  onTimerEnd, 
  onTimerStart,
  isPlaying,
  audioContext 
}) => {
  const [duration, setDuration] = useState(30);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [endSound, setEndSound] = useState(true);
  const [fadeOut, setFadeOut] = useState(true);
  const [fadeOutDuration, setFadeOutDuration] = useState(30); // seconds
  
  const timerRef = useRef(null);

  const playEndChime = useCallback(() => {
    if (!audioContext || !endSound) return;
    
    const frequencies = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    
    frequencies.forEach((freq, i) => {
      setTimeout(() => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        
        osc.frequency.value = freq;
        osc.type = 'sine';
        
        gain.gain.setValueAtTime(0, audioContext.currentTime);
        gain.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 0.1);
        gain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 1.5);
        
        osc.connect(gain);
        gain.connect(audioContext.destination);
        
        osc.start();
        osc.stop(audioContext.currentTime + 1.5);
      }, i * 300);
    });
  }, [audioContext, endSound]);

  const startTimer = useCallback(() => {
    if (!isPlaying) {
      toast.error("Start playing audio first");
      return;
    }
    
    setIsRunning(true);
    setTimeLeft(duration * 60);
    onTimerStart?.();
    toast.success(`Timer set for ${duration} minutes`);
  }, [duration, isPlaying, onTimerStart]);

  const stopTimer = useCallback(() => {
    setIsRunning(false);
    setTimeLeft(0);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const pauseTimer = useCallback(() => {
    setIsRunning(false);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const resumeTimer = useCallback(() => {
    if (timeLeft > 0) {
      setIsRunning(true);
    }
  }, [timeLeft]);

  useEffect(() => {
    if (!isRunning) return;

    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setIsRunning(false);
          playEndChime();
          onTimerEnd?.();
          toast.success("Session complete!");
          return 0;
        }
        
        // Trigger fade out
        if (fadeOut && prev === fadeOutDuration) {
          toast.info("Fading out...");
        }
        
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRunning, fadeOut, fadeOutDuration, playEndChime, onTimerEnd]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = timeLeft > 0 ? ((duration * 60 - timeLeft) / (duration * 60)) * 100 : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-cyan-400" />
          <h4 className="text-sm font-medium text-gray-300">Session Timer</h4>
        </div>
        {isRunning && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">
            Running
          </span>
        )}
      </div>

      {/* Timer Display */}
      <div className="glass-card p-4 text-center">
        <div className="text-4xl font-mono text-white mb-2">
          {timeLeft > 0 ? formatTime(timeLeft) : formatTime(duration * 60)}
        </div>
        
        {/* Progress bar */}
        {isRunning && (
          <div className="h-1 bg-white/10 rounded-full overflow-hidden mb-3">
            <motion.div
              className="h-full bg-gradient-to-r from-cyan-500 to-violet-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* Duration Presets */}
        {!isRunning && timeLeft === 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {presetDurations.map((preset) => (
              <motion.button
                key={preset.value}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setDuration(preset.value)}
                className={cn(
                  "px-3 py-1 text-xs rounded-lg transition-colors",
                  duration === preset.value
                    ? "bg-cyan-500 text-white"
                    : "bg-white/5 text-gray-400 hover:bg-white/10"
                )}
              >
                {preset.label}
              </motion.button>
            ))}
          </div>
        )}

        {/* Custom Duration Slider */}
        {!isRunning && timeLeft === 0 && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Custom</span>
              <span>{duration} min</span>
            </div>
            <Slider
              value={[duration]}
              onValueChange={([v]) => setDuration(v)}
              min={1}
              max={180}
              step={1}
              data-testid="timer-duration-slider"
            />
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center justify-center gap-3">
          {!isRunning && timeLeft === 0 && (
            <NeonButton onClick={startTimer} data-testid="start-timer" size="sm">
              <Play size={16} className="mr-1" />
              Start Timer
            </NeonButton>
          )}
          
          {isRunning && (
            <>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={pauseTimer}
                className="p-2 rounded-lg bg-yellow-500/20 text-yellow-400"
              >
                <Pause size={18} />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={stopTimer}
                className="p-2 rounded-lg bg-red-500/20 text-red-400"
              >
                <RotateCcw size={18} />
              </motion.button>
            </>
          )}
          
          {!isRunning && timeLeft > 0 && (
            <>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={resumeTimer}
                className="p-2 rounded-lg bg-green-500/20 text-green-400"
              >
                <Play size={18} />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={stopTimer}
                className="p-2 rounded-lg bg-red-500/20 text-red-400"
              >
                <RotateCcw size={18} />
              </motion.button>
            </>
          )}
        </div>
      </div>

      {/* Settings */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setEndSound(!endSound)}
            data-testid="toggle-end-sound"
            className={cn(
              "flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors",
              endSound ? "bg-cyan-500/20 text-cyan-400" : "bg-white/5 text-gray-500"
            )}
          >
            {endSound ? <Bell size={12} /> : <BellOff size={12} />}
            End Chime
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setFadeOut(!fadeOut)}
            data-testid="toggle-fade-out"
            className={cn(
              "flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors",
              fadeOut ? "bg-violet-500/20 text-violet-400" : "bg-white/5 text-gray-500"
            )}
          >
            Fade Out ({fadeOutDuration}s)
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimer;
